import {computed, Injectable, signal} from '@angular/core';

export interface MediaItem {
  url: string;
  type: 'image' | 'video';
  duration?: number;
}

export interface SlideshowData {
  audioResults: any[];
  briefDescription: string;
  slideshowImages: string[];
  slideshowMediaItems: MediaItem[];
  isVisible: boolean;
  isGamingMode?: boolean;
  gameContext?: string;
}

@Injectable({
  providedIn: 'root'
})
export class SlideshowDataService {
  readonly hasResults = computed(() => this.hasAudioResults() || this.hasMediaItems());
  // Private signals for internal state management
  private _audioResults = signal<any[]>([]);
  // Public readonly computed signals for external access
  readonly audioResults = computed(() => this._audioResults());
  // Computed helper properties
  readonly hasAudioResults = computed(() => this._audioResults().length > 0);
  private _briefDescription = signal<string>('');
  readonly briefDescription = computed(() => this._briefDescription());
  private _slideshowImages = signal<string[]>([]);
  readonly slideshowImages = computed(() => this._slideshowImages());
  private _slideshowMediaItems = signal<MediaItem[]>([]);
  readonly slideshowMediaItems = computed(() => this._slideshowMediaItems());
  readonly hasMediaItems = computed(() => this._slideshowMediaItems().length > 0);
  private _isVisible = signal<boolean>(false);
  readonly isVisible = computed(() => this._isVisible());
  private _resultsAvailable = signal<boolean>(false);
  readonly resultsAvailable = computed(() => this._resultsAvailable());
  private _fullApiAnalyzeResponse = signal<any>(null);
  readonly fullApiAnalyzeResponse = computed(() => this._fullApiAnalyzeResponse());
  private _currentAlternativePromptIndex = signal<number>(0);
  readonly currentAlternativePromptIndex = computed(() => this._currentAlternativePromptIndex());
  private _isGamingMode = signal<boolean>(false);
  readonly isGamingMode = computed(() => this._isGamingMode());
  private _gameContext = signal<string>('');
  readonly gameContext = computed(() => this._gameContext());

  // Event subjects for communication between components - none needed anymore
  // Slide-show component is now fully autonomous

  // Methods to update state
  setAudioResults(results: any[]): void {
    this._audioResults.set(results);
    this._resultsAvailable.set(results.length > 0);
  }

  setBriefDescription(description: string): void {
    this._briefDescription.set(description);
  }

  setSlideshowImages(images: string[]): void {
    this._slideshowImages.set(images);
  }

  setSlideshowMediaItems(items: MediaItem[]): void {
    this._slideshowMediaItems.set(items);
  }

  setVisible(visible: boolean): void {
    this._isVisible.set(visible);
  }

  setResultsAvailable(available: boolean): void {
    this._resultsAvailable.set(available);
  }

  setFullApiResponse(response: any): void {
    this._fullApiAnalyzeResponse.set(response);
  }

  setCurrentAlternativePromptIndex(index: number): void {
    this._currentAlternativePromptIndex.set(index);
  }

  setGamingMode(isGaming: boolean): void {
    this._isGamingMode.set(isGaming);
  }

  setGameContext(context: string): void {
    this._gameContext.set(context);
  }

  // Method to update all slideshow data at once
  updateSlideshowData(data: Partial<SlideshowData>): void {
    if (data.audioResults !== undefined) {
      this.setAudioResults(data.audioResults);
    }
    if (data.briefDescription !== undefined) {
      this.setBriefDescription(data.briefDescription);
    }
    if (data.slideshowImages !== undefined) {
      this.setSlideshowImages(data.slideshowImages);
    }
    if (data.slideshowMediaItems !== undefined) {
      this.setSlideshowMediaItems(data.slideshowMediaItems);
    }
    if (data.isVisible !== undefined) {
      this.setVisible(data.isVisible);
    }
    if (data.isGamingMode !== undefined) {
      this.setGamingMode(data.isGamingMode);
    }
    if (data.gameContext !== undefined) {
      this.setGameContext(data.gameContext);
    }
  }

  // Method to open slideshow with data
  openSlideshow(data: {
    audioResults: any[];
    briefDescription: string;
    slideshowImages?: string[];
    slideshowMediaItems?: MediaItem[];
    isGamingMode?: boolean;
    gameContext?: string;
  }): void {
    this.updateSlideshowData({
      ...data,
      slideshowImages: data.slideshowImages || [],
      slideshowMediaItems: data.slideshowMediaItems || [],
      isVisible: true
    });
  }


  // Method to reset all state
  resetState(): void {
    this._audioResults.set([]);
    this._briefDescription.set('');
    this._slideshowImages.set([]);
    this._slideshowMediaItems.set([]);
    this._isVisible.set(false);
    this._resultsAvailable.set(false);
    this._fullApiAnalyzeResponse.set(null);
    this._currentAlternativePromptIndex.set(0);
    this._isGamingMode.set(false);
    this._gameContext.set('');
  }

  // Event emission methods - none needed anymore
  // Slide-show component handles all its own modal interactions
} 
